// CTA copy variants. Index in this array == position in the deterministic
// hash assignment, so don't reorder existing entries — only append new ones
// (and add them to the WP plugin's CTA dropdown at the same time).
export const CTA_VARIANTS = ["A", "B", "C", "D", "E", "F"] as const;
export type CtaVariant = (typeof CTA_VARIANTS)[number];

export const CTA_COPY: Record<CtaVariant, string> = {
  A: "Hi! I'm Benji 👋 Got an event coming up?",
  B: "Quote in 60 seconds — ask Benji",
  C: "Need coffee for an event? Let's chat",
  D: "Save your event date — chat now ☕",
  E: "Custom quote, no pressure. Ask me anything.",
  F: "Booking fall dates now — check availability",
};

/**
 * FNV-1a 32-bit hash. Tiny, fast, deterministic across all platforms —
 * cryptographic strength isn't required because we just need an even
 * distribution across variant buckets.
 */
export function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Pick a variant for a given visitor. Stable per sessionId, so the visitor
 * sees the same copy on every reload until they clear localStorage.
 */
export function assignVariant(sessionId: string): CtaVariant {
  const idx = hashString(sessionId) % CTA_VARIANTS.length;
  return CTA_VARIANTS[idx] as CtaVariant;
}

/**
 * Resolve a variant from the WP plugin's CTA config + the visitor's
 * session. The config value can be a specific letter (force everyone to
 * that variant) or "auto" (let the hash decide).
 */
export function resolveVariant(configValue: string, sessionId: string): CtaVariant {
  const upper = (configValue ?? "").toUpperCase();
  if (CTA_VARIANTS.includes(upper as CtaVariant)) {
    return upper as CtaVariant;
  }
  return assignVariant(sessionId);
}

export const TRACK_EVENTS = ["shown", "opened", "messaged", "captured"] as const;
export type TrackEvent = (typeof TRACK_EVENTS)[number];

// ─── Launcher microinteraction variants ────────────────────────────────────
// Orthogonal A/B/C to the CTA copy experiment above. Same visitor sees both
// at the same time; the two are analyzed independently. Index in this array
// == position in the deterministic hash assignment, so don't reorder existing
// entries — only append new ones.
export const INTERACTION_VARIANTS = ["A", "B", "C"] as const;
export type InteractionVariant = (typeof INTERACTION_VARIANTS)[number];

export const INTERACTION_DESCRIPTIONS: Record<InteractionVariant, string> = {
  A: "Ripple + bobble — gold ring pulses outward every ~3.6s, button bobs up–down 3px every 4.2s",
  B: "Personality wiggle — Benji's face tilts side-to-side every ~7s, 1.5× faster on first mount",
  C: "Rotating speech-bubble teaser — bubble copy cycles through 3–4 short pitches every ~10s",
};

/**
 * Assign a launcher-interaction variant for a visitor. Salted with
 * `:interaction` so the assignment is INDEPENDENT of the CTA variant —
 * without the salt, sessions whose hash mod 6 fell in {0,1} would
 * systematically also fall in interactionVariant=A, breaking orthogonality.
 */
export function assignInteractionVariant(sessionId: string): InteractionVariant {
  const idx = hashString(sessionId + ":interaction") % INTERACTION_VARIANTS.length;
  return INTERACTION_VARIANTS[idx] as InteractionVariant;
}

/**
 * Resolve the launcher-interaction variant from the WP plugin's config + the
 * visitor's session. Config can be a specific letter (force everyone to that
 * variant) or "auto" / empty (let the hash decide).
 */
export function resolveInteractionVariant(
  configValue: string | null | undefined,
  sessionId: string,
): InteractionVariant {
  const upper = (configValue ?? "").toUpperCase();
  if (INTERACTION_VARIANTS.includes(upper as InteractionVariant)) {
    return upper as InteractionVariant;
  }
  return assignInteractionVariant(sessionId);
}
